import { useCallback, useMemo, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import { useQuery } from '@/api/query'
import { OnboardingContext } from './context'
import type { OnboardingValue } from './context'
import type { OnboardingState } from './steps'

/**
 * One read of the wizard, shared by every step and by the shell's setup banner.
 *
 * Mounted high enough that moving between steps does not re-request: the state
 * is one object and every write returns the whole of it, so the wizard needs
 * exactly one `GET` for a whole session at the keyboard.
 *
 * `adopted` shadows the query rather than replacing it, so a `reload()` that
 * fails leaves the wizard standing on what it already had — the same shape
 * `TenantProvider` uses, and for the same reason: a network blip mid-wizard
 * should not empty the screen somebody is typing into.
 */
export function OnboardingProvider({ children }: { children: ReactNode }): JSX.Element {
  const [adopted, setAdopted] = useState<OnboardingState | null>(null)

  const query = useQuery((signal) => call(() => api.GET('/api/v1/onboarding', { signal })), [])

  const adopt = useCallback((next: OnboardingState) => {
    setAdopted(next)
  }, [])

  const { refetch } = query
  const reload = useCallback(() => {
    setAdopted(null)
    refetch()
  }, [refetch])

  const value = useMemo<OnboardingValue>(() => {
    const state = adopted ?? query.state.data
    return {
      status: state ? 'ready' : query.state.status === 'error' ? 'error' : 'loading',
      failure: query.state.failure,
      state,
      adopt,
      reload,
    }
  }, [adopted, query.state, adopt, reload])

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}
